import { useState } from "react";
import { cr, inr } from "@/lib/wealth";

const BLANK = {
  name: "", description: "", owner_entity_id: "", ownership_form: "Individual",
  family_ownership_pct: 100, bucket: "safety", asset_class: "", liquidity_band: "immediate",
  valuation_method: "direct", quantity: "", unit_type: "", price_per_unit: "", direct_value: "",
  valuation_date: "", source_type: "manual", source_detail: "", annual_income: "", notes: "",
};

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="label-caps">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
  </label>
);

const cls = "mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm text-navy outline-none focus:border-plum disabled:opacity-60";

export const AssetForm = ({ config, entities, initial, onCancel, onSubmit }) => {
  const [f, setF] = useState(() => {
    if (!initial) return { ...BLANK, asset_class: config?.asset_classes?.items?.[0] || "" };
    return {
      ...BLANK, ...initial,
      quantity: initial.quantity ?? "", price_per_unit: initial.price_per_unit ?? "",
      direct_value: initial.direct_value ?? "", annual_income: initial.annual_income ?? "",
      owner_entity_id: initial.owner_entity_id || "",
    };
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const locked = !!initial?.system_managed;
  const unit = f.valuation_method === "unit_price";
  const preview = unit
    ? (Number(f.quantity) || 0) * (Number(f.price_per_unit) || 0)
    : Number(f.direct_value) || 0;

  const submit = () => onSubmit({
    ...f,
    family_ownership_pct: Number(f.family_ownership_pct) || 0,
    quantity: unit ? Number(f.quantity) : null,
    price_per_unit: unit ? Number(f.price_per_unit) : null,
    direct_value: unit ? null : Number(f.direct_value),
    annual_income: f.annual_income === "" ? null : Number(f.annual_income),
    owner_entity_id: f.owner_entity_id || null,
  });

  return (
    <div className="panel space-y-5 p-6" data-testid="asset-form">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <Field label="Asset name">
          <input value={f.name} onChange={(e) => set("name", e.target.value)} disabled={locked}
                 data-testid="asset-name" className={cls} />
        </Field>
        <Field label="Owner / entity">
          <select value={f.owner_entity_id} onChange={(e) => set("owner_entity_id", e.target.value)}
                  disabled={locked} data-testid="asset-owner" className={cls}>
            <option value="">Select an entity</option>
            {entities.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
          </select>
        </Field>
        <Field label="Ownership form">
          <select value={f.ownership_form} onChange={(e) => set("ownership_form", e.target.value)}
                  disabled={locked} data-testid="asset-ownership-form" className={cls}>
            {(config?.ownership_forms?.items || []).map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Family beneficial ownership %">
          <input type="number" min="0" max="100" value={f.family_ownership_pct} disabled={locked}
                 onChange={(e) => set("family_ownership_pct", e.target.value)}
                 data-testid="asset-ownership-pct" className={cls} />
        </Field>
        <Field label="Risk allocation bucket">
          <select value={f.bucket} onChange={(e) => set("bucket", e.target.value)} disabled={locked}
                  data-testid="asset-bucket" className={cls}>
            {(config?.buckets?.items || []).map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </Field>
        <Field label="Asset class">
          <select value={f.asset_class} onChange={(e) => set("asset_class", e.target.value)}
                  disabled={locked} data-testid="asset-class" className={cls}>
            <option value="">Select</option>
            {(config?.asset_classes?.items || []).map((c) => <option key={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Liquidity classification">
          <select value={f.liquidity_band} onChange={(e) => set("liquidity_band", e.target.value)}
                  disabled={locked} data-testid="asset-liquidity" className={cls}>
            {(config?.liquidity_bands?.items || []).map((b) => (
              <option key={b.key} value={b.key}>{b.label} — {b.definition}</option>
            ))}
          </select>
        </Field>
        <Field label="Valuation method">
          <select value={f.valuation_method} onChange={(e) => set("valuation_method", e.target.value)}
                  disabled={locked} data-testid="asset-method" className={cls}>
            {(config?.valuation_methods?.items || []).filter((m) => m.key !== "business")
              .map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            {locked && <option value="business">Business Valuation (system)</option>}
          </select>
        </Field>
        <Field label="Valuation / price date">
          <input type="date" value={(f.valuation_date || "").slice(0, 10)} disabled={locked}
                 onChange={(e) => set("valuation_date", e.target.value)}
                 data-testid="asset-valuation-date" className={cls} />
        </Field>

        {unit && (
          <>
            <Field label="Quantity / units">
              <input type="number" step="any" min="0" value={f.quantity} data-testid="asset-quantity"
                     onChange={(e) => set("quantity", e.target.value)} className={cls} />
            </Field>
            <Field label="Unit type">
              <input value={f.unit_type} placeholder="shares / units / grams" data-testid="asset-unit-type"
                     onChange={(e) => set("unit_type", e.target.value)} className={cls} />
            </Field>
            <Field label="Market price per unit (₹)" hint="Natural rupee value, e.g. 10200 per gram">
              <input type="number" step="any" min="0" value={f.price_per_unit} data-testid="asset-price"
                     onChange={(e) => set("price_per_unit", e.target.value)} className={cls} />
            </Field>
          </>
        )}
        {!unit && (
          <Field label="Direct / external value (₹)" hint="Natural rupee value, e.g. 12000000 for ₹1.2 Cr">
            <input type="number" step="any" min="0" value={f.direct_value} disabled={locked}
                   onChange={(e) => set("direct_value", e.target.value)}
                   data-testid="asset-direct-value" className={cls} />
          </Field>
        )}

        <Field label="Valuation source type">
          <select value={f.source_type} onChange={(e) => set("source_type", e.target.value)}
                  disabled={locked} data-testid="asset-source-type" className={cls}>
            {(config?.source_types?.items || []).map((s) => (
              <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
            ))}
          </select>
        </Field>
        <Field label="Source detail (provenance)">
          <input value={f.source_detail} onChange={(e) => set("source_detail", e.target.value)}
                 disabled={locked} placeholder="Bank statement / valuer / NAV provider"
                 data-testid="asset-source-detail" className={cls} />
        </Field>
        <Field label="Annual income / yield (₹)">
          <input type="number" step="any" value={f.annual_income} data-testid="asset-income"
                 onChange={(e) => set("annual_income", e.target.value)} className={cls} />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-4 rounded-xl bg-muted/40 px-5 py-4">
        <p className="label-caps">Gross current value</p>
        <p className="font-mono text-lg font-semibold tabular-nums text-navy" data-testid="asset-preview">
          {cr(preview)} <span className="text-xs font-normal text-muted-foreground">({inr(preview)})</span>
        </p>
        {unit && (
          <p className="text-[11px] text-muted-foreground">
            {f.quantity || 0} {f.unit_type || "units"} × {inr(Number(f.price_per_unit) || 0)}
          </p>
        )}
        {locked && (
          <p className="text-[11px] font-semibold text-plum">
            System-managed — the Business Value Engine owns this valuation
          </p>
        )}
      </div>

      <div className="flex gap-3">
        <button onClick={submit} data-testid="asset-save"
                className="rounded-full bg-plum px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-white hover:bg-plum/90">
          {initial ? "Save changes" : "Add asset"}
        </button>
        <button onClick={onCancel} data-testid="asset-cancel"
                className="rounded-full border border-border px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-navy hover:bg-muted">
          Cancel
        </button>
      </div>
    </div>
  );
};
